import {
  ArrowRight,
  Building2,
  CalendarDays,
  ChevronRight,
  MapPin,
  Menu,
  Radio,
  ShieldCheck,
  Smartphone,
  Trophy,
  Users,
  X,
} from 'lucide-react';
import {
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  Link,
  Navigate,
  Route,
  Routes,
  useNavigate,
} from 'react-router-dom';
import {
  loginWithGoogle,
  logoutSession,
  restoreSession,
  type BrowserSession,
} from './auth-session';
import { GoogleButton } from './google-button';

const fixtures = [
  {
    home: 'Kabul Lions',
    away: 'Aryana FC',
    time: 'LIVE',
    score: '2 – 1',
    live: true,
  },
  { home: 'Maiwand Athletic', away: 'Pamir Stars', time: '20:00', score: 'vs' },
  { home: 'Herat Youth', away: 'Ghor Stars', time: '21:15', score: 'vs' },
];

interface AuthContextValue {
  session: BrowserSession | null;
  status: 'loading' | 'ready';
  setSession: (session: BrowserSession | null) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('AuthContext is not available');
  return value;
}

function Header() {
  const [open, setOpen] = useState(false);
  const { session, status, setSession } = useAuth();
  const signOut = async () => {
    await logoutSession();
    setSession(null);
    setOpen(false);
  };
  return (
    <header className="site-header">
      <Link className="site-brand" to="/">
        <img src="/futsaly-mark.svg" alt="" />
        Futsaly
      </Link>
      <button
        className="nav-toggle"
        onClick={() => setOpen(!open)}
        aria-label="Toggle navigation"
      >
        {open ? <X /> : <Menu />}
      </button>
      <nav className={open ? 'open' : ''}>
        <a href="/#experience" onClick={() => setOpen(false)}>
          Experience
        </a>
        <a href="/#courts" onClick={() => setOpen(false)}>
          Courts
        </a>
        <a href="/#platform" onClick={() => setOpen(false)}>
          Platform
        </a>
        {session ? (
          <div className="account-nav">
            <span title={session.user.email}>{session.user.email}</span>
            <button type="button" onClick={() => void signOut()}>
              Sign out
            </button>
          </div>
        ) : status === 'ready' ? (
          <Link className="nav-cta" to="/login" onClick={() => setOpen(false)}>
            Log in
          </Link>
        ) : (
          <span className="nav-auth-loading" aria-label="Checking account" />
        )}
      </nav>
    </header>
  );
}

function LoginPage() {
  const { session, status, setSession } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const login = async (idToken: string) => {
    setBusy(true);
    setError('');
    try {
      const data = await loginWithGoogle(idToken);
      if (data.portal === 'owner')
        window.location.assign(
          import.meta.env.VITE_OWNER_URL ??
            (import.meta.env.DEV
              ? 'http://localhost:5175'
              : 'https://owner.futsaly.com'),
        );
      else if (data.portal === 'admin')
        window.location.assign(
          import.meta.env.VITE_ADMIN_URL ??
            (import.meta.env.DEV
              ? 'http://localhost:5173'
              : 'https://admin.futsaly.com'),
        );
      else {
        setSession(data);
        navigate('/', { replace: true });
      }
    } catch (cause) {
      setError((cause as Error).message);
      setBusy(false);
    }
  };
  if (status === 'loading') {
    return (
      <main className="web-login auth-loading">
        <span>Checking your Futsaly account…</span>
      </main>
    );
  }
  if (session) return <Navigate to="/" replace />;
  return (
    <>
      <Header />
      <main className="web-login">
        <section>
          <img src="/futsaly-mark.svg" />
          <span className="overline">One Futsaly account</span>
          <h1>Continue to your workspace.</h1>
          <p>
            Continue with Google to follow Futsaly on the web. New accounts are
            safely created as fan accounts.
          </p>
          {error && <div className="login-error">{error}</div>}
          <GoogleButton onCredential={login} disabled={busy} />
          <small>
            Court owners and privileged roles must first be invited by a Main
            Admin. Fans can use both this website and the mobile application.
          </small>
        </section>
      </main>
      <Footer />
    </>
  );
}

function HomePage() {
  const { session } = useAuth();
  return (
    <>
      <Header />
      <main>
        {session?.user.role === 'FAN' && (
          <section className="fan-welcome" aria-label="Signed-in fan options">
            <div className="fan-welcome-icon">
              <Smartphone />
            </div>
            <div>
              <strong>Welcome to Futsaly</strong>
              <p>
                You are signed in as {session.user.email}. Keep exploring
                everything on the website, or continue in the Futsaly mobile
                app.
              </p>
            </div>
            <div className="fan-welcome-actions">
              <a className="primary-action" href="futsaly://home">
                Open mobile app <ArrowRight size={17} />
              </a>
              <a href="#experience">Continue on website</a>
            </div>
          </section>
        )}
        <section className="hero">
          <div className="hero-copy">
            <div className="eyebrow">
              <Radio size={16} />
              Built for Afghan futsal
            </div>
            <h1>
              Every match.
              <br />
              <span>Every court.</span>
              <br />
              One home.
            </h1>
            <p>
              Follow live fixtures, explore tournaments, discover nearby futsal
              courts, and stay connected to the teams shaping the game.
            </p>
            <div className="hero-actions">
              {session ? (
                <a className="primary-action" href="#experience">
                  Explore Futsaly <ArrowRight size={18} />
                </a>
              ) : (
                <Link className="primary-action" to="/login">
                  Log in with Google <ArrowRight size={18} />
                </Link>
              )}
              <a className="text-action" href="#experience">
                See the platform <ChevronRight size={18} />
              </a>
            </div>
            <div className="trust-line">
              <ShieldCheck size={18} />
              <span>
                Designed for fans, teams, organizers, and venue owners.
              </span>
            </div>
          </div>
          <div className="scoreboard" aria-label="Futsaly match preview">
            <div className="scoreboard-head">
              <div>
                <span className="overline">Today</span>
                <h2>Afghanistan futsal</h2>
              </div>
              <span className="live-pill">3 matches</span>
            </div>
            <div className="date-strip">
              <span>Thu 27</span>
              <strong>Today</strong>
              <span>Sat 29</span>
            </div>
            {fixtures.map((match) => (
              <div className="fixture" key={match.home}>
                <span>{match.home}</span>
                <div>
                  <strong>{match.score}</strong>
                  <small className={match.live ? 'is-live' : ''}>
                    {match.time}
                  </small>
                </div>
                <span>{match.away}</span>
              </div>
            ))}
            <div className="scoreboard-foot">
              <span>Swipe through matchdays</span>
              <div className="dots">
                <i />
                <i className="active" />
                <i />
              </div>
            </div>
          </div>
        </section>
        <section className="feature-section" id="experience">
          <div className="section-heading">
            <span className="overline">Your matchday companion</span>
            <h2>
              The game moves fast.
              <br />
              Futsaly keeps up.
            </h2>
            <p>
              A focused experience for following local futsal without clutter.
            </p>
          </div>
          <div className="feature-grid">
            {[
              [
                CalendarDays,
                'Fixtures that feel alive',
                'Move smoothly through matchdays, see live minutes clearly, and return to today instantly.',
              ],
              [
                Trophy,
                'Tournaments in context',
                'Explore competitions, standings, match details, squads, events, and statistics.',
              ],
              [
                Users,
                'Teams worth following',
                'Keep up with the clubs and people building futsal communities across Afghanistan.',
              ],
            ].map(([Icon, title, copy]) => (
              <article className="feature-card" key={String(title)}>
                <div className="feature-icon">
                  <Icon />
                </div>
                <h3>{String(title)}</h3>
                <p>{String(copy)}</p>
              </article>
            ))}
          </div>
        </section>
        <section className="court-section" id="courts">
          <div className="court-image">
            <img src="/court-demo.png" alt="Indoor futsal court" />
            <span>
              <MapPin size={16} />
              Kabul
            </span>
          </div>
          <div className="court-copy">
            <span className="overline">Find your next game</span>
            <h2>Courts, availability, and details in one place.</h2>
            <p>
              Browse futsal venues by province, compare facilities, switch
              between list and map views, and find the right place for your
              group.
            </p>
            <ul>
              <li>
                <Building2 />
                Province-based discovery
              </li>
              <li>
                <MapPin />
                Clear location and venue details
              </li>
              <li>
                <CalendarDays />
                Booking-ready foundation
              </li>
            </ul>
            <a
              className="primary-action muted-action"
              href="mailto:omidezatyar9@gmail.com"
            >
              List your court <ArrowRight size={18} />
            </a>
          </div>
        </section>
        <section className="platform-section" id="platform">
          <div>
            <span className="overline">One connected platform</span>
            <h2>Built to grow with Afghan futsal.</h2>
          </div>
          <p>
            Futsaly is creating shared tools for fans, teams, tournament
            organizers, venue owners, referees, and federations—while keeping
            every role properly controlled.
          </p>
          <div className="role-row">
            <span>Fans</span>
            <span>Teams</span>
            <span>Organizers</span>
            <span>Courts</span>
            <span>Officials</span>
          </div>
        </section>
        <section className="final-cta">
          <img src="/futsaly-mark.svg" alt="" />
          <div>
            <span className="overline">Futsaly is taking shape</span>
            <h2>Afghan futsal deserves a digital home.</h2>
          </div>
          <a className="primary-action" href="mailto:omidezatyar9@gmail.com">
            Work with us <ArrowRight size={18} />
          </a>
        </section>
      </main>
      <Footer />
    </>
  );
}

function LegalPage({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <Header />
      <main className="legal">
        <span className="overline">Futsaly</span>
        <h1>{title}</h1>
        <p>
          <strong>Effective date:</strong> August 28, 2026
        </p>
        {children}
      </main>
      <Footer />
    </>
  );
}

function Footer() {
  return (
    <footer>
      <Link className="site-brand" to="/">
        <img src="/futsaly-mark.svg" alt="" />
        Futsaly
      </Link>
      <p>
        Futsaly is an independent platform under development and is not
        currently an official Afghanistan Football Federation service.
      </p>
      <div>
        <Link to="/privacy">Privacy</Link>
        <Link to="/terms">Terms</Link>
      </div>
    </footer>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/privacy"
        element={
          <LegalPage title="Privacy Policy">
            <h2>Information we collect</h2>
            <p>
              When you use Google sign-in, Futsaly may receive your verified
              email address and basic profile information needed to identify
              your account.
            </p>
            <h2>How information is used</h2>
            <p>
              We use account information to provide access, maintain security,
              personalize the service, and operate futsal features. We do not
              sell personal information.
            </p>
            <h2>Contact</h2>
            <p>
              Questions can be sent to{' '}
              <a href="mailto:omidezatyar9@gmail.com">omidezatyar9@gmail.com</a>
              .
            </p>
          </LegalPage>
        }
      />
      <Route
        path="/terms"
        element={
          <LegalPage title="Terms of Service">
            <h2>Using Futsaly</h2>
            <p>
              You agree to use Futsaly lawfully and to provide accurate
              information when managing teams, tournaments, matches, or venues.
            </p>
            <h2>Platform status</h2>
            <p>
              Futsaly is under active development. Features and test data may
              change as the platform moves toward public operation.
            </p>
            <h2>Contact</h2>
            <p>
              Questions can be sent to{' '}
              <a href="mailto:omidezatyar9@gmail.com">omidezatyar9@gmail.com</a>
              .
            </p>
          </LegalPage>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App() {
  const [session, setSession] = useState<BrowserSession | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready'>('loading');

  useEffect(() => {
    let active = true;
    void restoreSession().then((restored) => {
      if (!active) return;
      if (restored?.portal === 'admin') {
        window.location.replace(
          import.meta.env.VITE_ADMIN_URL ??
            (import.meta.env.DEV
              ? 'http://localhost:5173'
              : 'https://admin.futsaly.com'),
        );
        return;
      }
      if (restored?.portal === 'owner') {
        window.location.replace(
          import.meta.env.VITE_OWNER_URL ??
            (import.meta.env.DEV
              ? 'http://localhost:5175'
              : 'https://owner.futsaly.com'),
        );
        return;
      }
      setSession(restored);
      setStatus('ready');
    });
    return () => {
      active = false;
    };
  }, []);

  return (
    <AuthContext.Provider value={{ session, status, setSession }}>
      <AppRoutes />
    </AuthContext.Provider>
  );
}
